import React from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { PreviewSection } from "../shared";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { EstimateType } from "@/types/db/estimates";
import useAuth from "@/hooks/useAuth";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { formatCurrency } from "@/lib/format/currency";

export interface EstimateData {
  id: string;
  client_id?: string;
  project_id?: string;
  estimate_number?: string;
  estimate_date?: Date;
  expiry_date?: Date;
  amount?: number;
  status?: string;
  project?: {
    id?: string;
    name?: string;
    client_id?: string;
    client?: {
      id?: string;
      name?: string;
    };
  };
  meta_data?: {
    estimate_number?: string;
    estimate_date?: Date;
    expiry_date?: Date;
    amount_due?: number;
    line_items?: {
      description: string;
      unit_price: number;
      quantity: number;
      amount: number;
    }[];
    status?: "draft" | "sent" | "accepted" | "rejected" | "expired";
  };
}

export interface EstimateOverviewTabProps {
  estimate: EstimateData | null;
  loading?: boolean;
}

export function EstimateOverviewTab({ estimate, loading = false }: EstimateOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<EstimateType>();
  const { displayNames } = useAuth();

  // Unified data source - cast for consistent field access
  const displayData = (editing ? formData : estimate) as EstimateData | null;
  const disabled = loading || !editing;

  const formatDate = (date?: Date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString();
  };

  const calculateLineItemTotal = () => {
    const lineItems = displayData?.meta_data?.line_items || [];
    return lineItems.reduce((total, item) => total + (item.amount || 0), 0);
  };

  return (
    <View style={styles.tabContent}>
      <PreviewSection title="Estimate Details">
        <ThemedInput
          label="Estimate Number"
          value={displayData?.estimate_number || ""}
          onChangeText={(text) =>
            editing &&
            updateFormData({
              ...formData,
              estimate_number: text,
            } as any)
          }
          editable={!disabled}
          placeholder={disabled ? "—" : "Estimate number"}
        />
        <ThemedInput
          label="Estimate Date"
          value={formatDate(displayData?.estimate_date)}
          editable={false}
          placeholder="—"
        />
        <ThemedInput
          label="Expiry Date"
          value={formatDate(displayData?.expiry_date)}
          editable={false}
          placeholder="—"
        />
        <ThemedInput
          label="Amount"
          value={displayData?.amount ? formatCurrency(displayData.amount) : ""}
          onChangeText={(text) =>
            editing &&
            updateFormData({
              ...formData,
              amount: parseFloat(text.replace(/[^0-9.]/g, "")) || 0,
            } as any)
          }
          keyboardType="numeric"
          editable={!disabled}
          placeholder={disabled ? "—" : "$0.00"}
        />
      </PreviewSection>

      <PreviewSection title={`${displayNames?.projects?.name || "Project"} Details`}>
        <ClientInput
          value={displayData?.client_id || displayData?.project?.client_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.clients?.name || "Client"}
          onChange={(clientId) =>
            updateFormData({
              ...formData,
              client_id: clientId,
              project_id: undefined,
            } as any)
          }
        />
        <ProjectInput
          value={displayData?.project_id || null}
          clientId={displayData?.client_id || displayData?.project?.client_id}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.projects?.name || "Project"}
          onChange={(projectId) =>
            updateFormData({
              ...formData,
              project_id: projectId,
            } as any)
          }
        />
      </PreviewSection>

      <PreviewSection title="Line Items">
        {(() => {
          const lineItems = displayData?.meta_data?.line_items || [];

          if (lineItems.length === 0) {
            return <ThemedText style={{ opacity: 0.5 }}>No line items</ThemedText>;
          }

          return (
            <View>
              {lineItems.map((item, index) => (
                <View key={index} style={styles.lineItem}>
                  <View style={styles.lineItemHeader}>
                    <ThemedText type="defaultSemiBold">{item.description}</ThemedText>
                    <ThemedText type="defaultSemiBold">{formatCurrency(item.amount)}</ThemedText>
                  </View>
                  <ThemedText style={styles.lineItemDetail}>
                    {item.quantity} × {formatCurrency(item.unit_price)}
                  </ThemedText>
                </View>
              ))}
              <View style={styles.totalRow}>
                <ThemedText type="defaultSemiBold">Total: {formatCurrency(calculateLineItemTotal())}</ThemedText>
              </View>
            </View>
          );
        })()}

        {editing && (
          <ThemedText style={{ opacity: 0.5, fontStyle: "italic", marginTop: 8 }}>
            Line item editing will be implemented in a future update
          </ThemedText>
        )}
      </PreviewSection>
    </View>
  );
}

const styles = StyleSheet.create({
  tabContent: {
    flex: 1,
    gap: 16,
    padding: 16,
    overflowY: "scroll",
  },
  lineItem: {
    paddingVertical: 8,
  },
  lineItemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineItemDetail: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 2,
  },
  totalRow: {
    paddingTop: 12,
    marginTop: 8,
    borderTopWidth: 2,
    borderTopColor: "rgba(0,0,0,0.2)",
    alignItems: "flex-end",
  },
});
